import ModalWrapper from '@Components/Modals/ModalWrapper/ModalWrapper';
import AccessoriesServices from '@Services/Accessories.services.ts';
import { UnassignFormFields } from '@Types/Form.types';
import { formatRequired } from '@Utils/form.utils';
import { handleRequestError } from '@Utils/request.utils';
import { VALIDATION_RULES } from '@Utils/rule.utils.ts';
import { getUserWithLocation } from '@Utils/user.utils';
import { useApolloClient } from '@apollo/client';
import { ErrorDisplay, FreeText, Toast } from '@kg-internal/pv3-uikit';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { GetEmployeeByEmailDocument, GetEmployeeByEmailQuery } from '~/__generated__/graph';

type Props = {
  title: string;
  description?: string;
  showModal: boolean;
  onClose: () => void;
  onSuccess: () => void;
  accesoryId: string;
  employeeEmail: string;
};

const { UNASSIGN_OWNER } = VALIDATION_RULES;
const AccessoryUnassignModal = ({
  employeeEmail,
  accesoryId,
  showModal,
  onClose,
  onSuccess,
  title,
  description,
}: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const {
    handleSubmit,
    formState: { errors },
    reset,
    register,
  } = useForm<UnassignFormFields>();
  const client = useApolloClient();

  const onSubmitSuccess = () => {
    Toast.success('Accessory unassigned successfully.');
    onSuccess();
    reset();
    onClose();
  };

  const handleUnassignSubmit = async (data: UnassignFormFields) => {
    const { data: kgUser, error: portalError } = await client.query<GetEmployeeByEmailQuery>({
      query: GetEmployeeByEmailDocument,
      variables: { email: employeeEmail },
    });

    if (portalError) {
      Toast.warning('Could not fetch employee.');
    } else {
      let userWithLocation = {
        employeeEmail: employeeEmail,
        employeeLocation: '',
      };
      if (kgUser.person) {
        userWithLocation = getUserWithLocation(kgUser.person);
      } else {
        Toast.warning(
          `Could not find information for employee with email ${employeeEmail}. Asset will be unassigned but no email notification will be sent`,
        );
      }

      AccessoriesServices.unassignAccessory(accesoryId, {
        ...userWithLocation,
        reason: data.reason,
      })
        .then(() => {
          Toast.success('Accessory unassigned successfully.');
          onSubmitSuccess();
        })
        .catch((error) => handleRequestError(error, setErrorMessage, true));
    }
  };

  return (
    <div className='modal--fit-content modal--pt-25vh modal--w-30vw'>
      <ModalWrapper
        title={title}
        primaryButtonText='Confirm'
        show={showModal}
        handleClose={onClose}
        primaryAction={() => handleSubmit(handleUnassignSubmit)()}>
        {errorMessage ? (
          <ErrorDisplay inline details={errorMessage} />
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(handleUnassignSubmit)();
            }}>
            <div className='grid'>
              <div className='col-12'>{description && <p>{description}</p>}</div>
              <div className='col-12'>
                <FreeText
                  label={formatRequired('Reason')}
                  fullWidth
                  error={!!errors.reason}
                  errorMessage={errors.reason?.message}
                  {...register('reason', UNASSIGN_OWNER.REASON)}
                />
                <br />
              </div>
            </div>
          </form>
        )}
      </ModalWrapper>
    </div>
  );
};

export default AccessoryUnassignModal;
